import { Injectable, computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface EmbedInitPayload {
  token?: string;
  companyId?: string;
  apiUrl?: string;
  aiBaseUrl?: string;
  theme?: string;
  allowedOrigins?: string[];
}

@Injectable({ providedIn: 'root' })
export class EmbedSessionService {
  readonly token = signal('');
  readonly companyId = signal('');
  readonly apiUrl = signal(environment.apiUrl);
  readonly aiBaseUrl = signal(environment.aiBaseUrl);
  readonly theme = signal('');
  readonly allowedOrigins = signal<string[]>([...environment.allowedOrigins]);
  readonly initialized = signal(false);
  readonly hostOrigin = signal('');

  readonly hasAuth = computed(
    () => Boolean(this.token().trim() && this.companyId().trim())
  );

  readonly adminBaseUrl = computed(() => {
    const base = this.apiUrl().trim();
    if (!base) return '';
    return base.endsWith('/') ? `${base}admin/` : `${base}/admin/`;
  });

  applyInit(payload: EmbedInitPayload, origin: string): void {
    if (payload.token != null) this.token.set(String(payload.token));
    if (payload.companyId != null) this.companyId.set(String(payload.companyId));
    if (payload.apiUrl?.trim()) this.apiUrl.set(this.normalizeBase(payload.apiUrl));
    if (payload.aiBaseUrl?.trim()) {
      this.aiBaseUrl.set(this.normalizeBase(payload.aiBaseUrl));
    }
    if (payload.theme != null) this.theme.set(String(payload.theme));
    if (Array.isArray(payload.allowedOrigins) && payload.allowedOrigins.length) {
      this.allowedOrigins.set(payload.allowedOrigins.map(String));
    }
    this.hostOrigin.set(origin);
    this.initialized.set(true);
  }

  updateAuth(token?: string, companyId?: string): void {
    if (token != null) this.token.set(String(token));
    if (companyId != null) this.companyId.set(String(companyId));
  }

  clearAuth(): void {
    this.token.set('');
    this.companyId.set('');
  }

  isOriginAllowed(origin: string): boolean {
    const allow = this.allowedOrigins();
    if (!allow.length) {
      // No allowlist configured: accept in development only
      return !environment.production;
    }
    return allow.includes(origin) || allow.includes('*');
  }

  private normalizeBase(url: string): string {
    const trimmed = url.trim();
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  }
}
